import { useEffect, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useCityStore } from '../../city/cityStore';
import { useWorldStore } from '../../state/worldStore';
import { serverNow } from '../../services/worldSocket';
import { audioEngine } from '../../services/audioEngine';
import { api } from '../../services/api';
import { cutsceneBridge } from '../../city/cutscene';
import { cityAudio } from '../../city/stageBridge';
import { soundSources, KINDS } from '../../city/citySounds';
import { walled, falloff, setLevel } from '../../city/hearing';
import { takeAudio, releaseAudio, onAudio } from '../../services/audioPool';
import { driveBridge, useDriveStore } from '../../game/drive/driveStore';

// CitySounds — the street's music, each source a person's (city/citySounds says
// which are playing and whose). Heard by where you stand, like the plaza stage:
//
//   THE CLOSEST WINS CLEANLY (Malaki: "the closest one has to win cleanly and
//   the others duck"): the loudest source plays at its level, the next one sits
//   underneath at a fifth of its own, anything further is silent.
//   THE STATION STEPS ASIDE while one is close, and comes back after (the stage's rule).
//   THE STAGE OUTRANKS THE STREET: while a set is on and you can hear it, the
//   street's music fades away. And music never talks over people: a
//   conversation being heard nearby pushes it back (CityTalk).
//   Nothing while you are driving: the car you are in has its own radio.
//
// Each source plays its owner's station LIVE: what the station is playing now,
// at the bar it is at (its `seek_to_ms`), so everyone walking past hears the
// same thing. Elements come from the pool (services/audioPool: iOS).

const WALLED = 0.3;
const DUCK = 0.2;          // the runner-up, under the winner
const HOLD_ON = 0.3;       // this close to a source: the station steps aside…
const HOLD_OFF = 0.1;      // …and comes back once it is this quiet for a few seconds
const LET_GO_MS = 6000;    // a source out of earshot this long stops downloading

export default function CitySounds() {
  const camera = useThree((s) => s.camera);
  const stations = useRef(new Map());   // avatarId → slug | null (asked once each)
  const asking = useRef(new Set());
  const nows = useRef(new Map());       // slug → { url, title, artist, art, start, end } | { loading } | { none, until }
  const els = useRef(new Map());        // source id → { el, url, started, quietSince }
  const acc = useRef(0);
  const [speaker, setSpeaker] = useState(null);   // the court's speaker, drawn at its owner's feet { key, x, y, z, h }
  const held = useRef(false);
  const quietSince = useRef(0);

  // Probes only: stand anywhere on the street (headless tests can't walk there).
  useEffect(() => {
    if (!import.meta.env.DEV || typeof window === 'undefined' || !window.__look) return undefined;
    window.__look.standAt = (x, z, lookX, lookZ) => { camera.position.set(x, camera.position.y, z); if (lookX != null) camera.lookAt(lookX, camera.position.y, lookZ); };
    return () => { delete window.__look.standAt; };
  }, [camera]);

  // Leaving the street (a venue, the plan view): everything stops, the station comes back.
  useEffect(() => () => {
    for (const e of els.current.values()) releaseAudio(e.el);
    els.current.clear();
    if (held.current) { held.current = false; audioEngine.roomQuiet(false, 900, 'city-sounds').catch?.(() => {}); }
    useCityStore.setState({ soundNow: null });
  }, []);

  /** What a station is playing now (fetched when it is needed, again when its song ends). */
  const nowOf = (slug, t) => {
    const n = nows.current.get(slug);
    if (n && !n.loading && (n.none ? t < n.until : t < n.end - 300)) return n.none ? null : n;
    if (n?.loading) return n.prev || null;
    nows.current.set(slug, { loading: true, prev: n && !n.none ? n : null });
    api.stationNow(slug).then((d) => {
      const c = d?.current;
      const at = serverNow();
      if (!d?.playing || !c?.audio_url) { nows.current.set(slug, { none: true, until: at + 60000 }); return; }
      const dur = Number(c.duration_ms) || (Number(d.seek_to_ms) + Number(d.remaining_ms)) || 180000;
      const start = at - (Number(d.seek_to_ms) || 0);
      nows.current.set(slug, { url: c.audio_url, title: c.title || '', artist: c.artist || c.avatar_name || '', art: c.artwork_url || null, start, end: start + dur });
    }).catch(() => nows.current.set(slug, { none: true, until: serverNow() + 60000 }));
    return n && !n.none ? n : null;
  };

  useFrame((_, dt) => {
    acc.current += dt;
    if (acc.current < 0.25) return;
    acc.current = 0;
    const cs = useCityStore.getState();
    if (!cs.live) return;
    const t = serverNow();

    // Who has a station of their own (each resident asked once, a few at a time).
    for (const r of Object.values(cs.byId || {})) {
      const id = Number(r.avatarId);
      if (stations.current.has(id) || asking.current.has(id) || asking.current.size >= 3) continue;
      asking.current.add(id);
      api.artistRadio(id)
        .then((d) => stations.current.set(id, d?.station?.slug || null))
        .catch(() => stations.current.set(id, null))
        .finally(() => asking.current.delete(id));
    }

    const me = (cutsceneBridge.active && cutsceneBridge.at) || camera.position;
    const sources = soundSources(cs, t, driveBridge.active ? [] : (driveBridge.parked || []), useDriveStore.getState().driving, stations.current);
    // The court's speaker is a thing you can see, not just hear.
    const sp = sources.find((s) => s.kind === 'court');
    const spKey = sp ? `${sp.id}:${sp.x.toFixed(2)}:${sp.z.toFixed(2)}` : null;
    if ((speaker?.key || null) !== spKey) setSpeaker(sp ? { key: spKey, x: sp.x, y: sp.y ?? 0, z: sp.z } : null);

    const heard = sources.map((s) => {
      const K = KINDS[s.kind];
      const d = Math.hypot(me.x - s.x, me.z - s.z);
      const v = falloff(d, K.full, K.far, K.peak ?? 0.85) * (d > K.full && d < K.far && walled(me.x, me.z, s.x, s.z, K.skipEnd) ? WALLED : 1);
      return { s, v };
    }).filter((h) => h.v > 0.01).sort((a, b) => b.v - a.v);

    // What the street gives way to: a live set within earshot, people talking nearby, driving.
    const stageGive = driveBridge.active ? 0 : Math.max(0, 1 - cityAudio.stagePresence / 0.3);
    // A freestyle's beat is the bed under the voices; everything else steps back for people talking.
    const give = (s) => stageGive * (KINDS[s.kind].bed ? 1 : (cityAudio.speech ? 0.45 : 1));
    const want = new Map();
    if (heard[0]) want.set(heard[0].s.id, { h: heard[0], level: heard[0].v * give(heard[0].s) });
    if (heard[1]) want.set(heard[1].s.id, { h: heard[1], level: heard[1].v * give(heard[1].s) * DUCK });

    let top = 0; let showing = null;
    for (const [id, { h, level }] of want) {
      // A source's own track (a freestyle's beat), or what its owner's station is playing.
      const n = h.s.play || nowOf(h.s.slug, t);
      if (!n) continue;
      let e = els.current.get(id);
      if (e && e.url !== n.url) { releaseAudio(e.el); els.current.delete(id); e = null; }   // the station moved on
      if (!e) {
        const el = takeAudio(n.url);
        el.muted = true;
        try { el.load(); } catch { /* loads on play */ }
        e = { el, url: n.url, started: false, quietSince: 0 };
        els.current.set(id, e);
      }
      const off = (t - n.start) / 1000;
      if (level > 0.01) {
        e.quietSince = 0;
        if (!e.started) {
          e.started = true;
          try { e.el.currentTime = Math.max(0, off); } catch { /* on metadata */ }
          onAudio(e.el, 'loadedmetadata', () => { try { e.el.currentTime = Math.max(0, (serverNow() - n.start) / 1000); } catch { /* ignore */ } }, { once: true });
          e.el.play().catch(() => {});
        } else if (e.el.readyState >= 2 && Math.abs(e.el.currentTime - off) > 0.8) {
          try { e.el.currentTime = Math.max(0, off); } catch { /* ignore */ }
        }
      }
      // Paused from the player (Hud ⏯): still the source on show, just silent.
      setLevel(e.el, useWorldStore.getState().cityMusicOff ? 0 : level);
      if (level > top) { top = level; showing = { h, n }; }
    }
    if (import.meta.env.DEV && typeof window !== 'undefined') window.__citySounds = { sources: sources.map((s) => ({ id: s.id, kind: s.kind, owner: s.owner.name, slug: s.slug, x: +s.x.toFixed(1), z: +s.z.toFixed(1) })), mix: [...want].map(([id, w]) => ({ id, level: +w.level.toFixed(3) })), playing: [...els.current].map(([id, e]) => ({ id, paused: e.el.paused, muted: e.el.muted, vol: +e.el.volume.toFixed(2) })) };   // probes only
    // Out of earshot (or no longer wanted) for a while: let it go.
    for (const [id, e] of els.current) {
      const w = want.get(id);
      if (w && w.level > 0.01) continue;
      setLevel(e.el, 0);
      if (!e.quietSince) e.quietSince = Date.now();
      else if (Date.now() - e.quietSince > LET_GO_MS) { releaseAudio(e.el); els.current.delete(id); }
    }

    // The station steps aside while a source is close, and comes back after.
    if (top >= HOLD_ON && !held.current) { held.current = true; audioEngine.roomQuiet(true, 900, 'city-sounds').catch?.(() => {}); }
    if (held.current) {
      if (top > HOLD_OFF) quietSince.current = 0;
      else if (!quietSince.current) quietSince.current = Date.now();
      else if (Date.now() - quietSince.current > 4000) { held.current = false; quietSince.current = 0; audioEngine.roomQuiet(false, 900, 'city-sounds').catch?.(() => {}); }
    }

    // What the player bar says while one is clearly heard.
    const now = top > 0.15 && showing
      ? { id: showing.h.s.id, title: showing.n.title, artist: showing.n.artist, art: showing.n.art, where: KINDS[showing.h.s.kind].where(showing.h.s.owner.name) }
      : null;
    const cur = cs.soundNow;
    if ((cur?.id || null) !== (now?.id || null) || (cur?.title || null) !== (now?.title || null)) useCityStore.setState({ soundNow: now });
  });

  return speaker ? <Boombox at={speaker} /> : null;
}

/** A small speaker on the ground (a person is 0.75 tall), its light pulsing a little. */
function Boombox({ at }) {
  const led = useRef();
  useFrame(({ clock }) => { if (led.current) led.current.emissiveIntensity = 1.2 + 0.8 * Math.abs(Math.sin(clock.elapsedTime * 3.1)); });
  return (
    <group position={[at.x, at.y, at.z]}>
      <mesh position={[0, 0.065, 0]} castShadow>
        <boxGeometry args={[0.24, 0.13, 0.08]} />
        <meshStandardMaterial color="#1c1d22" roughness={0.55} metalness={0.25} />
      </mesh>
      {[-0.065, 0.065].map((x) => (
        <mesh key={x} position={[x, 0.065, 0.041]}>
          <circleGeometry args={[0.042, 20]} />
          <meshStandardMaterial color="#2c2e35" roughness={0.4} metalness={0.4} />
        </mesh>
      ))}
      <mesh position={[0, 0.118, 0.041]}>
        <planeGeometry args={[0.05, 0.012]} />
        <meshStandardMaterial ref={led} color="#000" emissive="#ffb25e" emissiveIntensity={1.5} />
      </mesh>
      <mesh position={[0, 0.15, 0]}>
        <boxGeometry args={[0.16, 0.012, 0.012]} />
        <meshStandardMaterial color="#3a3c44" roughness={0.5} metalness={0.6} />
      </mesh>
    </group>
  );
}
